import {
  BoxProps,
  Button,
  Flex,
  FormControl,
  Link,
  Text,
  Textarea,
  useBoolean,
} from '@chakra-ui/react'
import {
  GeneralParamsDto,
  InsuranceParamsDto,
  InsuranceParamsPropertyDto,
  InsuranceParamsTravelDto,
} from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import { useContext, useEffect, useState } from 'react'

import { Card, CardBody } from '../../components/Card'
import { FormLabel } from '../../components/FormLabel'
import { Loading } from '../../components/Loading'
import { stepsContext } from '../../context/StepsContext'
import { useApp } from '../../hooks/useApp'
import { useUrl as useDocumentUrl } from '../../hooks/useDocuments'
import { Email, Note, Print, Save } from '../../icons'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { formatDate } from '../../utils/formatDate'
import { EmailAddress } from '../shared/EmailAddress'
import { MobilePhone } from '../shared/MobilePhone'
import { InsuranceInfoBasic } from './InsuranceInfoBasic'
import { InsuranceInfoBroker } from './InsuranceInfoBroker'
import { InsuranceInfoDetailsProperty } from './InsuranceInfoDetailsProperty'
import { InsuranceInfoDetailsTravel } from './InsuranceInfoDetailsTravel'
import { InsuranceInfoDetailsVehicle } from './InsuranceInfoDetailsVehicle'
import { InsuranceInfoFooter } from './InsuranceInfoFooter.1'
import { InsuranceInfoItem } from './InsuranceInfoItem'
import { InsuranceInfoNote } from './InsuranceInfoNote'
import { InsuranceInfoWrapper } from './InsuranceInfoWrapper'
const carkulkaUrl = process.env.REACT_APP_CARKULKA_URL

export interface InsuranceInfoBasicProps extends BoxProps {
  isSticky: boolean
}

export type InsuranceInfoFooterProps = BoxProps

export interface InsuranceInfoWrapperProps extends BoxProps {
  isSticky: boolean
}

const getOffset = (element: Element | null) => element?.getBoundingClientRect()?.y

export const InsuranceInfo = () => {
  const documentUrl = useDocumentUrl()
  const {
    insuranceParams: insuranceParamsGeneric,
    isLoadingData,
    insuranceParamsId: paramsId,
    isLoading,
  } = useInsuranceParamsContext()
  const { app, product } = useApp()

  const insuranceParamsVehicle =
    product == Product.VehicleInsurance || product == Product.VehicleLeasingInsurance
      ? (insuranceParamsGeneric as InsuranceParamsDto)
      : undefined

  const insuranceParamsTravel =
    product == Product.TravelInsurance
      ? (insuranceParamsGeneric as InsuranceParamsTravelDto)
      : undefined

  const insuranceParamsProperty =
    product == Product.PropertyInsurance
      ? (insuranceParamsGeneric as InsuranceParamsPropertyDto)
      : undefined

  const { step: currentStep } = useContext(stepsContext)

  const insuranceInfoWrapper = document.querySelector('.js-insurance-info-wrapper')
  const [isSticky, setIsSticky] = useState(false)
  const [showNoteTextarea, setShowNoteTextarea] = useBoolean(false)
  const [note, setNote] = useState('')
  const shouldStick = isSticky && !showNoteTextarea

  useEffect(() => {
    const listenToScroll = () => {
      const heightToHide = getOffset(insuranceInfoWrapper) || 0

      setIsSticky(heightToHide <= 0)
    }

    const scrollInterval = setInterval(listenToScroll, 16)

    return () => clearInterval(scrollInterval)
  }, [insuranceInfoWrapper])

  const handleSaveNote = (event: any) => {
    const value = event.target.value.trim()

    setNote(value)
    setShowNoteTextarea.off()
  }

  let generalParams: GeneralParamsDto | undefined = undefined
  let insuranceParamsAvailable = false
  if (insuranceParamsVehicle) {
    generalParams = insuranceParamsVehicle.generalParams
    insuranceParamsAvailable = true
  } else if (insuranceParamsTravel) {
    generalParams = insuranceParamsTravel.generalParams
    insuranceParamsAvailable = true
  } else if (insuranceParamsProperty) {
    generalParams = insuranceParamsProperty.generalParams
    insuranceParamsAvailable = true
  }

  const createdBy = generalParams?.createdBy
  const phEmail = insuranceParamsGeneric?.policyHolder.person.email
  let phPhone = insuranceParamsGeneric?.policyHolder.person.mobilePhone
  // předvolbu zobrazujeme pouze u čísel, která nejsou tuzemská
  if (
    phPhone &&
    insuranceParamsGeneric?.policyHolder.person.mobilePhonePrefix &&
    insuranceParamsGeneric?.policyHolder.person.mobilePhonePrefix != '+420'
  ) {
    phPhone = insuranceParamsGeneric?.policyHolder.person.mobilePhonePrefix + phPhone
  }
  if (!phPhone) {
    phPhone = insuranceParamsGeneric?.policyHolder.person.phone
  }

  return (
    <InsuranceInfoWrapper className="js-insurance-info-wrapper" isSticky={shouldStick}>
      <Card
        sx={{
          bgColor: shouldStick ? { lg: 'transparent' } : 'white',
          boxShadow: shouldStick ? { base: 'card', lg: 'none' } : 'card',
        }}
      >
        <CardBody sx={{ display: 'grid', p: 4, gap: 4 }}>
          {(!insuranceParamsAvailable || isLoading) && <Loading large={true} />}

          {insuranceParamsAvailable && (
            <>
              <InsuranceInfoBasic isSticky={shouldStick} className="insurance-info">
                {paramsId && <InsuranceInfoItem label="Nabídka pojištění" value={paramsId} />}

                {generalParams?.insuranceStartDate && (
                  <InsuranceInfoItem
                    label="Počátek pojištění"
                    value={formatDate({ date: new Date(generalParams.insuranceStartDate) })}
                  />
                )}

                {insuranceParamsVehicle && (
                  <InsuranceInfoDetailsVehicle {...insuranceParamsVehicle} />
                )}

                {insuranceParamsTravel && <InsuranceInfoDetailsTravel {...insuranceParamsTravel} />}

                {insuranceParamsProperty && (
                  <InsuranceInfoDetailsProperty {...insuranceParamsProperty} />
                )}

                {phEmail && (
                  <InsuranceInfoItem
                    label="E-mail pojistníka"
                    value={<EmailAddress email={phEmail} showIcon={false} />}
                  />
                )}

                {phPhone && (
                  <InsuranceInfoItem
                    label="Telefon pojistníka"
                    value={<MobilePhone mobilePhone={phPhone} showIcon={false} />}
                  />
                )}

              </InsuranceInfoBasic>

              <InsuranceInfoFooter
                as="footer"
                sx={{ transition: 'all 100ms', opacity: shouldStick ? { lg: 0 } : 1 }}
              >
                {createdBy && (
                  <InsuranceInfoBroker
                    broker={createdBy}
                    date={new Date(generalParams?.created!)}
                  />
                )}

                <Flex sx={{ gap: 2 }} className="insurance-info__footer-buttons">
                  {currentStep == 3 &&
                    (app === 'carkulka' || app == 'cestovko' || app == 'chaloupka') && (
                      <Button
                        as={Link}
                        href={`${carkulkaUrl}/Mailing/GetCalculationEmail/${paramsId}?order=1&report=0&okapps=true`}
                        target="_blank"
                        variant="outline"
                        size="sm"
                        type="button"
                      >
                        <Email color="secondary.500" />
                        E-mail
                      </Button>
                    )}

                  {currentStep == 3 &&
                    (app === 'carkulka' || app == 'cestovko' || app == 'chaloupka') && (
                      <Button
                        as={Link}
                        href={`${documentUrl}/GetOffersPdf?paramsId=${paramsId}`}
                        target="_blank"
                        variant="outline"
                        size="sm"
                        type="button"
                      >
                        <Print color="secondary.500" />
                        Tisk
                      </Button>
                    )}

                  {false && (
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={setShowNoteTextarea.toggle}
                    >
                      <Note color="secondary.500" />
                      Napsat poznámku
                    </Button>
                  )}

                  {showNoteTextarea && (
                    <Button
                      variant="solid"
                      colorScheme="primary"
                      size="sm"
                      type="button"
                      onClick={setShowNoteTextarea.off}
                    >
                      <Save color="white" />
                      Uložit poznámku
                    </Button>
                  )}
                </Flex>
              </InsuranceInfoFooter>
            </>
          )}

          {showNoteTextarea && (
            <FormControl variant="fullWidth">
              <FormLabel>Poznámka ke kalkulaci</FormLabel>
              <Textarea autoFocus defaultValue={note} onBlur={handleSaveNote} />
            </FormControl>
          )}

          {note && !showNoteTextarea && (
            <InsuranceInfoNote
              sx={{ transition: 'all 100ms', opacity: isSticky ? { lg: 0 } : 1 }}
              onClick={setShowNoteTextarea.on}
            >
              <Text variant="textMuted">{note}</Text>
            </InsuranceInfoNote>
          )}
        </CardBody>
      </Card>
    </InsuranceInfoWrapper>
  )
}
